import { ReactNode, useRef, useState, useCallback, useMemo } from 'react';
import { IModal, IModalApi, IModalConfirmOptions, IModalMessageOptions, IModalOptions } from '@vivli/shared/infrastructure/interface';
import { v4 as uuid } from 'uuid';
import { ConfirmDialogModalComponent, MessageModalComponent } from '@vivli/shared/components';

export const useModalApi = () => {
    const modalsRef = useRef<IModal[]>([]);
    const [modals, setModals] = useState<IModal[]>([]);

    /** Only update state if the reference content actually changed */
    const updateState = useCallback(() => {
        const current = modalsRef.current;
        setModals((prev) => {
            if (prev === current) return prev; // No change
            return [...current];
        });
    }, []);

    const addModal = useCallback(
        (modal: IModal) => {
            modalsRef.current.push(modal);
            updateState();
        },
        [updateState]
    );

    const removeModal = useCallback(
        (modalId: string) => {
            modalsRef.current = modalsRef.current.filter((m) => m.id !== modalId);
            updateState();
        },
        [updateState]
    );

    const clearModals = useCallback(() => {
        modalsRef.current = [];
        updateState();
    }, [updateState]);

    const custom = useCallback(
        (component: ReactNode, options?: IModalOptions): string => {
            const id = uuid();
            addModal({ component, id, visible: true, ...options });
            return id;
        },
        [addModal]
    );

    const hide = useCallback(
        (id?: string) => {
            modalsRef.current = modalsRef.current.map((modal) => {
                if (!id || modal.id === id) {
                    return { ...modal, visible: false };
                }
                return modal;
            });
            updateState();
        },
        [updateState]
    );

    const show = useCallback(
        (id: string) => {
            modalsRef.current = modalsRef.current.map((modal) => {
                if (modal.id === id) {
                    return { ...modal, visible: true };
                }
                return modal;
            });
            updateState();
        },
        [updateState]
    );

    const dismiss = useCallback(
        (id?: string) => {
            if (id) {
                removeModal(id);
            } else {
                clearModals();
            }
        },
        [removeModal, clearModals]
    );

    const confirm = useCallback(
        (message: ReactNode, options?: IModalConfirmOptions): string => {
            const modalId = uuid();

            const handleClose = (confirmed: boolean, comment?: string, selections?: any, dropdownListSelection?: any) => {
                if (confirmed) {
                    options?.onConfirm?.({ comment, selections, dropdownListSelection });
                } else {
                    options?.onCancel?.();
                }
                dismiss(modalId);
            };

            addModal({
                id: modalId,
                visible: true,
                ...options,
                component: <ConfirmDialogModalComponent {...options} message={message} onClose={handleClose} />
            });

            return modalId;
        },
        [addModal, dismiss]
    );

    const prompt = useCallback(
        (message: ReactNode, promptDefaultText?: string, options?: IModalConfirmOptions): string => {
            return confirm(message, {
                ...options,
                showPrompt: true,
                promptDefaultText
            });
        },
        [confirm]
    );

    const message = useCallback(
        (
            message: ReactNode,
            {
                showLogo = true,
                showBackground = true,
                showContinueButton = true,
                showLoadingSpinner,
                style,
                messageStyle,
                dismissOnClickOutside,
                title,
                onClick
            }: IModalMessageOptions = {}
        ): string => {
            const modalId = uuid();

            const handleClick = () => {
                dismiss(modalId);
            };

            const handleOverlayClick = () => {
                if (dismissOnClickOutside === true) dismiss(modalId);
                onClick?.();
            };

            addModal({
                id: modalId,
                visible: true,
                closeOnOverlayClick: dismissOnClickOutside,
                showBackground,
                onOverlayClick: () => onClick?.(),
                component: (
                    <MessageModalComponent
                        message={message}
                        onClick={handleClick}
                        onOverlayClick={handleOverlayClick}
                        showContinueButton={showContinueButton}
                        showLogo={showLogo}
                        showLoadingSpinner={showLoadingSpinner}
                        style={{ padding: '40px', ...style }}
                        messageStyle={messageStyle}
                        title={title}
                    />
                )
            });

            return modalId;
        },
        [addModal, dismiss]
    );

    const error = useCallback(
        (error: ReactNode) => {
            message(error, {
                showBackground: false,
                showContinueButton: true,
                showLogo: true,
                messageStyle: { color: 'red' }
            });
        },
        [message]
    );

    // Stable API reference: the callbacks are all useCallback-stable and `modals` is intentionally
    // excluded, so `modalApi` never changes identity. The changing `modals` array is returned
    // separately and provided through ModalStateContext, so adding/removing a modal re-renders only
    // the modal host — not every consumer of the modal API. (DEV-485 re-render fix)
    const modalApi: IModalApi = useMemo(
        () => ({
            custom,
            hide,
            show,
            dismiss,
            confirm,
            message,
            error,
            prompt
        }),
        [custom, hide, show, dismiss, confirm, message, error, prompt]
    );

    return { modalApi, modals };
};
